<template>
    <v-layout row wrap>
        <v-flex xs12 v-if="loading">
            <v-progress-linear :indeterminate="true"></v-progress-linear>
        </v-flex>
        <v-flex xs12 v-if="!loading">
            <v-card flat>
                <v-card-title class="primary white--text headline">
                    KPI
                    <v-spacer></v-spacer>
                    <date-range-selector v-model="dateRange"  @change="handleDateChange()"></date-range-selector>
                </v-card-title>
                <v-layout row wrap>
                    <v-flex xs12>
                        <v-layout row wrap>
                            <v-flex xs6 md3>
                                <value-box :value="bpi" unit="kWh/m²" title="Annualized Building Performance Index"
                                           icon="fa-smile" class="green-icon"
                                           explanation>
                                    <p>
                                        Annualized Building Performance Index or Energy Use Intensity (EUI) is calculated by dividing the annualized total energy consumption for the current/selected range ("{{dateRange.start}} to {{dateRange.end}}") with gross floor area of the building.
                                    </p>
                                    <p>
                                        It must be noted that the total energy consumption in this case is the sum of energy consumption of all equipment that has been mapped to the analytics platform. It does not include systems like fire alarms, lifts and escalators, plumbing and drainage etc.
                                    </p>
                                    <p>
                                        Annualized total energy consumption = ((total energy consumption for the current/selected range) / (number of days in the current/selected range)) * 365
                                    </p>
                                    <p>
                                        High Annualized Building Performance Index indicates inefficiency in equipment operation. According to EMSD the total site EUI for commercial buildings with centralized cooling is 132 kWh/m2/annum. HVAC systems consume about 68% of the total site energy, we can assume HVAC EUI benchmark to be about 90 kWh/m2/annum.
                                    </p>
                                    <p>
                                        References:
                                    </p>
                                    <ul>
                                        <li><a class="white--text" target="_blank" href="https://ecib.emsd.gov.hk/en/indicator_cmc.htm">EMSD EUI</a></li>
                                        <li><a class="white--text" target="_blank" href="https://www.researchgate.net/publication/315416147_A_Study_on_Energy_Performance_of_30_Commercial_Office_Buildings_in_Hong_Kong">HVAC breakdown for office buildings</a></li>
                                    </ul>
                                </value-box>
                            </v-flex>
                            <v-flex xs6 md3>
                                <value-box :value="oci" unit="%" title="Occupant Comfort Index"
                                           :icon="oci>50 ? 'fa-smile': 'fa-meh'"
                                           v-bind:class="{ 'green-icon': oci>50}"
                                           explanation
                                >
                                    <p>Occupant Comfort Index is defined as the % of AHUs with ΔSp lower than 2°C in the current/selected date range ("{{dateRange.start}} to {{dateRange.end}}"). ΔSp is the mean absolute difference between CDT_SP (cool duct setpoint) and CD_TEM (cool duct actual temperature).</p>
                                    <p>The higher the occupant comfort index the higher % of AHUs are following the setpoint and meeting demand requirements from the space. </p>
                                    <p>According to ASHRAE Standard 55 Thermal Environmental Conditions for Occupancy, the ideal range for temperature is 22 - 27 °C. As most AHUs are designed to supply air within this range, Higher Occupant Comfort Index will provide a ensure occupants or tenants in the space are thermally comfortable. </p>
                                    <p>
                                        References:
                                    </p>
                                    <ul>
                                        <li><a class="white--text" target="_blank" href="http://arco-hvac.ir/wp-content/uploads/2015/11/ASHRAE-55-2010.pdf ">ASHRAE STANDARD 55: Thermal Environmental Conditions for Human Occupancy</a></li>
                                    </ul>
                                </value-box>
                            </v-flex>
                            <v-flex xs6 md3>
                                <value-box :value="ehi" use-int unit=""
                                           title="Maintenance Fault Index" :icon="ehi<10 ? 'fa-smile': 'fa-meh'"
                                           v-bind:class="{ 'green-icon': ehi<10}"
                                           explanation
                                >
                                    <p>
                                        Defined as the number of critical or high severity maintenance type faults on critical equipment during the current/selected date range ("{{dateRange.start}} to {{dateRange.end}}"). Chiller Plant Equipment and Air Handling Units (AHU) are considered as critical equipment as they have the highest power consumption and are expensive to repair and maintain.
                                    </p>
                                    <p>
                                        Higher maintenance fault index indicates inefficiency in maintenance of the Chiller Plant and AHU equipment.
                                    </p>
                                    <p>Recommendations for maintenance and rectification of maintenance related defects are provided on the recommended action section under each notification or on notification breakdown. Recommendations for rectifications of maintenance faults are as per ASHRAE maintenance standard 180-2018. </p>
                                    <p>
                                        Standard 180 establishes minimum HVAC inspection and maintenance requirements that preserve a system's ability to achieve acceptable thermal comfort, energy efficiency and indoor air quality in new and existing commercial buildings.
                                    </p>
                                    <p>
                                        Being proactive in rectifying these faults will lead to continuous improvement to workflows, increased uptime and reduced spending on unnecessary repairs.
                                    </p>
                                    <p>
                                        References:
                                    </p>
                                    <ul>
                                        <li><a class="white--text" target="_blank" href="https://www.ashrae.org/technical-resources/bookstore/standards-180-and-211">ASHRAE STANDARD 180: Standard Practice for Inspection and Maintenance of Commercial Building HVAC Systems</a></li>
                                    </ul>
                                </value-box>
                            </v-flex>
                            <v-flex xs6 md3>
                                <value-box :value="powerDiff" unit="%"
                                           title="Energy Saving"
                                           :icon="powerDiff>0 ? 'fa-smile': 'fa-meh'"
                                           v-bind:class="{ 'green-icon': powerDiff>0 }"
                                           explanation
                                >
                                    <p>
                                        Computed by comparing the % difference between energy consumption of the current/selected date range ("{{dateRange.start}} to {{dateRange.end}}") with that of the baseline range ("{{pastRange.start}} to {{pastRange.end}}").
                                    </p>
                                    <p>
                                        Higher Year on Year (YoY) energy savings indicates increased HVAC equipment operational efficiency. The analytics software continuously provides opportunities that improve energy efficiency and HVAC asset life while reducing downtime.
                                    </p>
                                </value-box>
                            </v-flex>

                        </v-layout>
                    </v-flex>
                    <v-flex xs12>
                        <v-layout row wrap>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="chillerOnData" disable-responsive :data="chillerOnData" :layout="chillerOnLayout" :options="{}"/>
                            </v-flex>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="chillerPowerData" disable-responsive :data="chillerPowerData" :layout="chillerPowerLayout" :options="{}"/>
                            </v-flex>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="chillerCOPData" disable-responsive :data="chillerCOPData" :layout="chillerCOPLayout" :options="{}"/>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                    <v-flex xs12>
                        <v-layout row wrap>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="ahuOnData" disable-responsive :data="ahuOnData" :layout="ahuOnLayout" :options="{}"/>
                            </v-flex>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="ahuPowerData" disable-responsive :data="ahuPowerData" :layout="ahuPowerLayout" :options="{}"/>
                            </v-flex>
                            <v-flex xs12 md4>
                                <vue-plotly v-if="ahuDiffData" disable-responsive :data="ahuDiffData"
                                            @click="handleDiffClick"
                                            :layout="ahuDiffLayout" :options="{}"/>
                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script>

    import moment from 'moment'
    import {callAPI,getNotification} from '@/helper'
    import VuePlotly from "../components/VuePlotly";
    import ValueBox from "../components/ValueBox";
    import DateRangeSelector from "../components/DateRangeSelector";


    export default {
        name: 'HKLKPI',
        components: {VuePlotly,ValueBox,DateRangeSelector},
        data () {
            return {
                menu:false,
                oci:null,
                ehi:null,
                baseDate:  moment().subtract(15, "days"),
                endDate:  moment().subtract(1, "days"),
                dateRange:  {start:moment().subtract(15, "days").format("YYYY-MM-DD"),end:moment().subtract(1, "days").format("YYYY-MM-DD")},
                pastRange:  [],
                loading:true,
                ahuOnData:null,
                chillers:[],
                pumps:[],
                ahus:[],
                powerDiff:null,
                bpi:null,
                ahuOnLayout: {
                    title: "AHU Run Time (hr)",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        l:210
                    }
                },
                ahuDiffData:null,
                ahuDiffLayout: {
                    title: "ZoneTemp ΔSp (absΔ°C)",
                    showlegend: false,
                    xaxis:{
                        range:[0,3],
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        l:210
                    }
                },
                chillerOnData:null,
                chillerOnLayout: {
                    title: "Chiller Run Time (hr)",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        l:210
                    }
                },
                chillerPowerData:null,
                chillerPowerLayout: {
                    title: "Chiller (kWh)",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        l:210
                    }
                },
                chillerCOPData:null,
                chillerCOPLayout: {
                    title: "Chiller COP",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        l:210
                    }
                },
                ahuPowerData:null,
                ahuPowerLayout: {
                    title: "AHU (kWh)",
                    showlegend: false,
                    xaxis:{
                        showgrid:false,
                        ticks:'',
                        showticklabels:false
                    },
                    margin:{
                        l:210
                    }
                },
            }
        },
        methods:{
            handleDateChange(){
                this.baseDate = moment(this.dateRange.start)
                this.endDate = moment(this.dateRange.end)
                this.getData()
            },
            addArrays(ar1, ar2){
                let ar3 = [];
                for(let i in ar1)
                    if(!ar2[i]){
                        ar3.push(ar1[i]);
                    } else {
                        ar3.push(ar1[i] + ar2[i]);
                    }
                return ar3;
            },
            handleDiffClick(data){
                this.$router.push({
                    path: '/sensors',
                    query: {
                        variableX: 'CDT_SP',
                        variableY: 'CD_TEM',
                        equipment: data.points[0].y,
                        rangeStart: this.dateRange.start,
                        rangeEnd: this.dateRange.end,
                    }
                })
            },
            getData(){
                let me=this;
                me.loading=true;
                me.bpi=null
                me.oci=null
                me.ehi=null
                me.powerDiff=null
                me.chillers=[]
                me.pumps=[]
                me.ahus=[]
                let currentRange=[me.baseDate.startOf('day').format('YYYY-MM-DD HH:mm:ss'),me.endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss')]
                let pastRange=[moment(currentRange[0]).subtract(1, "month").startOf('day').format('YYYY-MM-DD HH:mm:ss'),moment(currentRange[1]).subtract(1, "month").endOf('day').format('YYYY-MM-DD HH:mm:ss')]
                me.pastRange={start:moment(currentRange[0]).subtract(1, "month").startOf('day').format('YYYY-MM-DD'),end:moment(currentRange[1]).subtract(1, "month").endOf('day').format('YYYY-MM-DD')}
                for (let i in this.$store.state.pointsListStore.pointsList.equipment_details) {
                    let equip = this.$store.state.pointsListStore.pointsList.equipment_details[i]
                    if(equip.equipment_type.toLowerCase().startsWith('chiller')){
                        me.chillers.push(equip.equipment_name)
                    } else if(equip.equipment_type==="ECFAN"||equip.equipment_type==="AHU"){
                        me.ahus.push(equip.equipment_name)
                    }
                }
                callAPI('data2/',
                    {
                        'filters': {
                            'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                            'equipment_type_regex': '^chiller',
                            'sensor_names': ["on",'Power'],
                            'date_range':currentRange,
                        },
                        "pipeline": [
                            {"type": "agg", "agg": "D", "agg_op": "sum"}
                        ]
                    }
                ).then(function(result){
                    let chillerNames=[]
                    let chillerOn=[]
                    let chillerPower=[]
                    for (let chillerName of me.chillers) {
                        chillerNames.push(chillerName)
                        if(result.data[chillerName+' - on']){
                            chillerOn.push(result.data[chillerName+' - on'].reduce((sum, x) => sum + x)/me.$store.state.pointsListStore.pointsList.sampling_freq)
                        } else {
                            chillerOn.push(0)
                        }
                        if (result.data[chillerName + ' - Power']) {
                            chillerPower.push(result.data[chillerName + ' - Power'].reduce((sum, x) => sum + x) / me.$store.state.pointsListStore.pointsList.sampling_freq)
                        } else {
                            chillerPower.push(0)
                        }
                    }
                    me.chillerOnData=[{
                        y:chillerNames,
                        x:chillerOn,
                        type: 'bar',
                        marker: {
                            color: 'rgba(255, 127, 14,0.5)',
                        },
                        orientation: 'h'
                    }]
                    me.chillerPowerData=[{
                        y:chillerNames,
                        x:chillerPower,
                        type: 'bar',
                        marker: {
                            color: 'rgba(214, 39, 40,0.5)',
                        },
                        orientation: 'h'
                    }]
                    me.loading=false
                })
                callAPI('data2/',
                    {
                        'filters': {
                            'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                            'equipment_type_regex': '^chiller',
                            'sensor_names': ["Cooling_Load","Power","on"],
                            'date_range':currentRange,
                        }
                    }
                ).then(function(result){
                    let chillerNames=[]
                    let chillerCOP=[]
                    for (let chillerName of me.chillers) {
                        chillerNames.push(chillerName)

                        if(result.data[chillerName+' - Cooling_Load']&&result.data[chillerName+' - Cooling_Load'].length>0&&
                            result.data[chillerName+' - Power']&&result.data[chillerName+' - Power'].length>0&&
                            result.data[chillerName+' - on']&&result.data[chillerName+' - on'].length>0){
                            let fullCL=0
                            let fullPower=0
                            for(let index in result.data[chillerName+' - on']){
                                if(result.data[chillerName+' - on'][index]>0){
                                    fullCL+=result.data[chillerName+' - Cooling_Load'][index]
                                    fullPower+=result.data[chillerName+' - Power'][index]
                                }
                            }
                            if(fullPower>0){
                                chillerCOP.push(fullCL/fullPower)
                            } else {
                                chillerCOP.push(0)
                            }
                        } else {
                            chillerCOP.push(0)
                        }
                    }
                    me.chillerCOPData=[{
                        y:chillerNames,
                        x:chillerCOP,
                        type: 'bar',
                        marker: {
                            color: 'rgba(44, 160, 44, 0.5)',
                        },
                        orientation: 'h'
                    }]
                })
                if(me.ahus.length>0) {
                    callAPI('data2/',
                        {
                            'filters': {
                                'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                                'equipment_type_regex': 'ecfan|ahu',
                                'sensor_names': ['Power', 'Cooling_Valve_ABS_F_L', 'CDT_SP', 'CD_TEM'],
                                'date_range': currentRange,
                            }
                        }
                    ).then(function (result) {
                        let increm = 1 / me.$store.state.pointsListStore.pointsList.sampling_freq
                        let ahuNames = []
                        let ahuPowerNames = []
                        let ahuPower = []
                        let ahuOn = []
                        let ahuDiff = []
                        for (let ahuName of me.ahus) {
                            if (result.data[ahuName + ' - Power']) {
                                ahuPowerNames.push(ahuName)
                                ahuPower.push(result.data[ahuName + ' - Power'].reduce((sum, x) => sum + x) / me.$store.state.pointsListStore.pointsList.sampling_freq)
                            }
                            if (result.data[ahuName + ' - Cooling_Valve_ABS_F_L']) {
                                ahuNames.push(ahuName)
                                let ontime = 0
                                let diffArray = []
                                for (let flowindex in result.data[ahuName + ' - Cooling_Valve_ABS_F_L']) {
                                    let flow = result.data[ahuName + ' - Cooling_Valve_ABS_F_L'][flowindex]
                                    if (flow > 0) {
                                        ontime = ontime + increm
                                        if (result.data[ahuName + ' - CDT_SP'] && result.data[ahuName + ' - CD_TEM']) {
                                            diffArray.push(Math.abs(result.data[ahuName + ' - CD_TEM'][flowindex] - result.data[ahuName + ' - CDT_SP'][flowindex]))
                                        }
                                    }
                                }
                                ahuOn.push(ontime)
                                if (diffArray.length == 0) {
                                    ahuDiff.push(0)
                                } else {
                                    ahuDiff.push(diffArray.reduce((p, c) => p + c, 0) / diffArray.length)
                                }
                            }
                        }
                        me.oci = (ahuDiff.filter(x => x < 2).length / ahuDiff.length) * 100
                        me.ahuOnData = [{
                            y: ahuNames,
                            x: ahuOn,
                            type: 'bar',
                            marker: {
                                color: 'rgba(31, 119, 180,0.5)',
                            },
                            orientation: 'h'
                        }]
                        me.ahuOnLayout.height = Math.max(400, 25 * ahuNames.length)
                        me.ahuDiffData = [{
                            y: ahuNames,
                            x: ahuDiff,
                            type: 'bar',
                            marker: {
                                color: 'rgba(148, 103, 189,0.5)',
                            },
                            orientation: 'h'
                        }]
                        me.ahuDiffLayout.height = Math.max(400, 25 * ahuNames.length)
                        me.ahuPowerData = [{
                            y: ahuPowerNames,
                            x: ahuPower,
                            type: 'bar',
                            marker: {
                                color: 'rgba(140, 86, 75,0.5)',
                            },
                            orientation: 'h'
                        }]
                        me.ahuPowerLayout.height = Math.max(400, 25 * ahuPowerNames.length)

                    })
                }
                callAPI('data2/',
                    {
                        'filters': {
                            'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                            'sensor_names': ["Power"],
                            'date_range':currentRange,
                        },
                        "pipeline": [
                            {"type": "agg_h", "agg_op": "sum"},
                            {"type": "agg", "agg": "D", "agg_op": "sum"}
                        ]
                    }
                ).then(function(resc){
                    let currentPower=resc.data.sum.reduce((sum, x) => sum + x)/me.$store.state.pointsListStore.pointsList.sampling_freq
                    callAPI('data2/',
                        {
                            'filters': {
                                'facility_name': me.$store.state.pointsListStore.pointsList.facility_name,
                                'sensor_names': ["Power"],
                                'date_range':pastRange,
                            },
                            "pipeline": [
                                {"type": "agg_h", "agg_op": "sum"},
                                {"type": "agg", "agg": "D", "agg_op": "sum"}
                            ]
                        }
                    ).then(function(resp){
                        let pastPower=resp.data.sum.reduce((sum, x) => sum + x)/me.$store.state.pointsListStore.pointsList.sampling_freq
                        me.powerDiff=((pastPower-currentPower)/pastPower)*100
                        me.bpi=(currentPower*365/31122.5184)/(me.endDate.endOf('day').diff(me.baseDate.startOf('day'),'days')+1)
                    })
                })
                getNotification({
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                    date_range:currentRange,
                }).then(function(notifResult) {
                    let rangeFaults = notifResult.result
                    me.ehi=rangeFaults.filter(x=>x.topic==="maintenance"&&x.equipment_type!=="VAV"&&(x.severity==="high"||x.severity==="critical")).length
                })

            }
        },
        beforeMount() {
            let maxDate=this.$store.state.pointsListStore.pointsList.data_max_date;
            if(maxDate){
                this.baseDate=moment(maxDate).subtract(15, "days")
                this.endDate=moment(maxDate).subtract(1, "days")
                this.dateRange={start:this.baseDate.format('YYYY-MM-DD'),end:this.endDate.format('YYYY-MM-DD')}
            }
            this.getData();
        }



    }
</script>
